import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Tag,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiCheckCircle, FiFileText } from "react-icons/fi";
import {
  bffApi,
  EspdResponseDataType,
  type EspdResponseDto,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
  type SelectionCriterionDto,
  type SupplierPublicBidQualificationResponseDto,
  type ViewDocumentAsOwnerDto,
} from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import { composeCriterionKey as criterionKey } from "../../../../../common/qualification/criterionKey";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
} from "../../../../../common/documents/UppyUploader/FileRow";
import {
  removeQualificationEvidenceDocument as removeEvidenceDocumentFromResponses,
  useUppyUploader,
} from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { isPublicBidEditable } from "../publicBidEditability";
import {
  emptyEspd,
  removeEspdAnswer,
  setEspdAnswer,
} from "../espd/useEspdAnswers";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}


export const QualificationStep = ({
  bidId,
  event,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { state, updateField, fieldStatus } = useEditor<
    PublicBidDto,
    PublicBidPatchDto
  >(bidId);
  const readOnly = !isPublicBidEditable(state);
  const espd: EspdResponseDto = state.espdResponse ?? emptyEspd();
  const alphaLeadRequirement = useMemo(() => {
    const alpha = (event.applicableQualificationCriteria ?? [])[0];
    return alpha?.requirementGroups[0]?.requirements.find(
      (r) => r.responseDataType === EspdResponseDataType.Indicator,
    );
  }, [event.applicableQualificationCriteria]);
  const criteria = event.selectionCriteria ?? [];
  const qualificationResponses = state.qualificationResponses ?? [];

  const saveQualificationStep = (
    nextResponses: SupplierPublicBidQualificationResponseDto[],
    alphaSatisfied: boolean,
  ) => {
    const patch: PublicBidPatchDto = { qualificationResponses: nextResponses };
    if (alphaLeadRequirement) {
      patch.espdResponse = alphaSatisfied
        ? setEspdAnswer(espd, alphaLeadRequirement.id, { indicator: true })
        : removeEspdAnswer(espd, alphaLeadRequirement.id);
    }
    updateField(patch, { debounceMs: 300 });
  };
  const upsertQualificationResponse = (
    criterion: SelectionCriterionDto,
    patch: Partial<SupplierPublicBidQualificationResponseDto>,
  ) => {
    const key = criterionKey(criterion);
    const current = qualificationResponses.find(
      (response) => response.criterionKey === key,
    );
    const hasEvidenceDocumentIdsPatch = Object.prototype.hasOwnProperty.call(
      patch,
      "evidenceDocumentIds",
    );
    const hasEvidenceDocumentsPatch = Object.prototype.hasOwnProperty.call(
      patch,
      "evidenceDocuments",
    );
    const evidenceDocumentIds = hasEvidenceDocumentIdsPatch
      ? patch.evidenceDocumentIds
      : current?.evidenceDocumentIds;
    const evidenceDocuments = hasEvidenceDocumentsPatch
      ? patch.evidenceDocuments
      : current?.evidenceDocuments;
    const nextResponse: SupplierPublicBidQualificationResponseDto = {
      criterionKey: key,
      description: current?.description ?? null,
      ...(hasEvidenceDocumentIdsPatch || (evidenceDocumentIds?.length ?? 0) > 0
        ? { evidenceDocumentIds: evidenceDocumentIds ?? [] }
        : {}),
      ...(hasEvidenceDocumentsPatch || (evidenceDocuments?.length ?? 0) > 0
        ? { evidenceDocuments: evidenceDocuments ?? [] }
        : {}),
      ...patch,
    };
    const nextResponses = [
      ...qualificationResponses.filter(
        (response) => response.criterionKey !== key,
      ),
      nextResponse,
    ];
    const allCriteriaComplete = criteria.every((c) => {
      const response =
        c === criterion
          ? nextResponse
          : nextResponses.find((r) => r.criterionKey === criterionKey(c));
      return qualificationResponseComplete(c, response);
    });
    saveQualificationStep(nextResponses, allCriteriaComplete);
  };
  const removeQualificationEvidence = async (
    criterion: SelectionCriterionDto,
    document: ViewDocumentAsOwnerDto,
  ) => {
    const key = criterionKey(criterion);
    const nextResponses = removeEvidenceDocumentFromResponses(
      qualificationResponses,
      key,
      document.id,
    );
    const allCriteriaCompleteAfterRemoval = criteria.every((c) => {
      const response = nextResponses.find(
        (r) => r.criterionKey === criterionKey(c),
      );
      return qualificationResponseComplete(c, response);
    });
    saveQualificationStep(nextResponses, allCriteriaCompleteAfterRemoval);
  };
  const reloadBid = useCallback(() => {
    void dispatch(
      bffApi.endpoints.getPublicBid.initiate({ bidId }, { forceRefetch: true }),
    ).unwrap();
  }, [bidId, dispatch]);
  const isSaving =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending" ||
    fieldStatus("qualificationResponses") === "saving" ||
    fieldStatus("qualificationResponses") === "pending";
  const answeredCount = criteria.filter((criterion) => {
    const response = qualificationResponses.find(
      (r) => r.criterionKey === criterionKey(criterion),
    );
    return (response?.description ?? "").trim().length > 0;
  }).length;
  const evidenceRequiredCount = criteria.filter(
    (criterion) => criterion.requiresEvidence,
  ).length;
  const evidenceProvidedCount = criteria.filter((criterion) => {
    const response = qualificationResponses.find(
      (r) => r.criterionKey === criterionKey(criterion),
    );
    return (
      criterion.requiresEvidence && qualificationEvidenceCount(response) > 0
    );
  }).length;
  const allCriteriaComplete =
    criteria.length > 0 &&
    criteria.every((criterion) => {
      const response = qualificationResponses.find(
        (r) => r.criterionKey === criterionKey(criterion),
      );
      return qualificationResponseComplete(criterion, response);
    });

  return (
    <Box pb="8" pt="6">
      <Flex
        align={{ base: "start", md: "center" }}
        justify="space-between"
        gap="3"
        mb="2"
        flexWrap="wrap"
      >
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Qualification requirements")}</Heading>
          <Badge
            bg="smPrimarySofter"
            color="smPrimary"
            px="2"
            py="0.5"
            borderRadius="md"
            textTransform="none"
          >
            {t("Buyer criteria")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        <HStack spacing="3" flexWrap="wrap">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={answeredCount === criteria.length ? "smSuccess" : "smMuted"}
          >
            {t("{{answered}} of {{total}} answered", {
              answered: answeredCount,
              total: criteria.length,
            })}
          </Text>
          {evidenceRequiredCount > 0 && (
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color={
                evidenceProvidedCount === evidenceRequiredCount
                  ? "smSuccess"
                  : "smWarning"
              }
            >
              {t(
                "{{completed}} of {{total}} documentation requirements complete",
                {
                  completed: evidenceProvidedCount,
                  total: evidenceRequiredCount,
                },
              )}
            </Text>
          )}
        </HStack>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="4">
        {t(
          "Answer the buyer's qualification requirements. Upload documentation where required.",
        )}
      </Text>

      <Box
        borderWidth="1px"
        borderColor={
          allCriteriaComplete ? "smSuccessSoftBorder" : "smPrimarySoft"
        }
        bg={allCriteriaComplete ? "smSuccessSoft" : "smPrimarySofter"}
        rounded="lg"
        p="4"
        mb="6"
      >
        <Stack spacing="1">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={allCriteriaComplete ? "smSuccess" : "smPrimary"}
          >
            {allCriteriaComplete
              ? t("All qualification requirements are complete")
              : t("Answer each buyer qualification requirement")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t(
              "Provide a text answer for each requirement and upload documentation where the buyer requires it.",
            )}
          </Text>
        </Stack>
      </Box>

      <Stack spacing="3" mb="6">
        {criteria.map((criterion, index) => {
          const key = criterionKey(criterion);
          const response = qualificationResponses.find(
            (r) => r.criterionKey === key,
          );
          const cardState = qualificationCardState(criterion, response);
          const evidenceCount = qualificationEvidenceCount(response);
          return (
            <Box
              key={key}
              borderWidth="1px"
              borderColor={cardState.borderColor}
              rounded="lg"
              px="4"
              py="3"
              bg={cardState.bg}
              boxShadow={cardState.boxShadow}
            >
              <Stack spacing="3">
                <HStack
                  justify="space-between"
                  align="start"
                  spacing="3"
                  flexWrap="wrap"
                >
                  <HStack spacing="2" align="center" flexWrap="wrap">
                    <Tag size="sm" colorScheme="gray" variant="subtle">
                      {t(criterion.type)}
                    </Tag>
                    <Text fontWeight="semibold" fontSize="sm">
                      {criterion.name}
                    </Text>
                  </HStack>
                  <HStack spacing="2" flexWrap="wrap" justify="flex-end">
                    {criterion.requiresEvidence && (
                      <QualificationEvidenceStatusBadge
                        evidenceCount={evidenceCount}
                        hasAnswer={
                          (response?.description ?? "").trim().length > 0
                        }
                      />
                    )}
                    {cardState.labelKey && (
                      <Tag
                        size="sm"
                        bg={cardState.tagBg}
                        color={cardState.tagColor}
                        borderRadius="md"
                        flexShrink="0"
                        textTransform="none"
                      >
                        <Icon as={cardState.icon} mr="1.5" aria-hidden />
                        {t(cardState.labelKey)}
                      </Tag>
                    )}
                  </HStack>
                </HStack>

                {criterion.description && (
                  <TipTap
                    content={criterion.description}
                    editable={false}
                    hasBorder={false}
                    padding="0"
                    maxHeight=""
                  />
                )}

                <Textarea
                  rows={3}
                  placeholder={String(
                    t("Answer the qualification requirement"),
                  )}
                  isReadOnly={readOnly}
                  value={response?.description ?? ""}
                  onChange={(e) =>
                    upsertQualificationResponse(criterion, {
                      description: e.target.value,
                    })
                  }
                />

                {criterion.requiresEvidence && (
                  <QualificationEvidenceDocuments
                    bidId={bidId}
                    criterionKey={key}
                    criterionIndex={index}
                    documents={response?.evidenceDocuments ?? []}
                    readOnly={readOnly}
                    removeDocument={(document) =>
                      removeQualificationEvidence(criterion, document)
                    }
                    reloadBid={reloadBid}
                  />
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Box mt="8">
        <StepNavigation
          onPrevious={previousStep}
          onNext={nextStep}
          nextLabel={nextStepLabel}
        />
      </Box>
    </Box>
  );
};

const qualificationEvidenceCount = (
  response: SupplierPublicBidQualificationResponseDto | undefined,
): number => {
  const ids = new Set<string>();
  response?.evidenceDocumentIds?.forEach((id) => ids.add(id));
  response?.evidenceDocuments?.forEach((document) => ids.add(document.id));
  return ids.size;
};

const qualificationCardState = (
  criterion: SelectionCriterionDto,
  response: SupplierPublicBidQualificationResponseDto | undefined,
) => {
  if (
    criterion.requiresEvidence &&
    (response?.description ?? "").trim().length > 0 &&
    qualificationEvidenceCount(response) === 0
  ) {
    return {
      borderColor: "smWarningSoftBorder",
      bg: "smWarningSoft",
      boxShadow: "sm",
      tagBg: "smWarningSoft",
      tagColor: "smWarning",
      labelKey: null,
      icon: FiAlertCircle,
    };
  }
  if ((response?.description ?? "").trim().length > 0) {
    return {
      borderColor: "smSuccessSoftBorder",
      bg: "smSuccessSoft",
      boxShadow: "sm",
      tagBg: "smSuccessSoft",
      tagColor: "smSuccess",
      labelKey: "Answered",
      icon: FiCheckCircle,
    };
  }
  return {
    borderColor: "smBorder",
    bg: "smBackground",
    boxShadow: "none",
    tagBg: "smBackgroundSecondary",
    tagColor: "smMuted",
    labelKey: null,
    icon: FiCheckCircle,
  };
};

const QualificationEvidenceStatusBadge = ({
  evidenceCount,
  hasAnswer,
}: {
  evidenceCount: number;
  hasAnswer: boolean;
}) => {
  const { t } = useTranslation();
  const label =
    evidenceCount > 0
      ? t("Documentation provided")
      : hasAnswer
        ? t("Documentation missing")
        : t("Documentation required");

  return (
    <Badge
      bg={evidenceCount > 0 ? "smSuccessSoft" : "smWarningSoft"}
      color={evidenceCount > 0 ? "smSuccess" : "smWarning"}
      px="2"
      py="0.5"
      borderRadius="md"
      textTransform="none"
    >
      {label}
    </Badge>
  );
};

const qualificationResponseComplete = (
  criterion: SelectionCriterionDto,
  response: SupplierPublicBidQualificationResponseDto | undefined,
): boolean =>
  (response?.description ?? "").trim().length > 0 &&
  (!criterion.requiresEvidence || qualificationEvidenceCount(response) > 0);

const QualificationEvidenceDocuments = ({
  bidId,
  criterionKey,
  criterionIndex,
  documents: responseDocuments,
  readOnly,
  removeDocument,
  reloadBid,
}: {
  bidId: string;
  criterionKey: string;
  criterionIndex: number;
  documents: ViewDocumentAsOwnerDto[];
  readOnly: boolean;
  removeDocument: (document: ViewDocumentAsOwnerDto) => Promise<void>;
  reloadBid: () => void;
}) => {
  const { t } = useTranslation();
  const {
    uppy,
    allFiles,
    documents,
    setDocuments,
    removeUpload,
    reloadEntity,
  } = useUppyUploader({
    entityType: "PublicBidQualificationEvidence",
    entityId: bidId,
    qualificationCriterionKey: criterionKey,
    reloadEntity: reloadBid,
  });

  useEffect(() => {
    setDocuments(responseDocuments);
  }, [responseDocuments, setDocuments]);

  return (
    <Box
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      bg="smBackground"
      rounded="md"
      p="3"
    >
      <HStack spacing="2" mb="2" align="center">
        <Icon as={FiFileText} color="smWarning" aria-hidden />
        <Text fontSize="sm" fontWeight="semibold">
          {t("Documentation")}
        </Text>
      </HStack>
      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        removeUploadedDocument={removeDocument}
        isTagsColumnVisible={false}
        isCategoryColumnVisible={false}
        canDeleteDocuments={!readOnly}
        canEditDocumentNames={!readOnly}
        documentUploadConfig={
          !readOnly
            ? {
                uppy,
                dropTargetId: `qualification-evidence-${bidId}-${criterionIndex}`,
                dropTargetHelperText: t(
                  "Drag and drop files here, or click to browse",
                ),
                uploads: allFiles.filter(
                  isIncompleteFileUpload,
                ) as IncompleteFileUpload[],
                removeUpload,
              }
            : undefined
        }
        refetchDocuments={() => {
          reloadEntity();
        }}
      />
    </Box>
  );
};
